import { EMPTY_LIST, EMPTY_STRING } from '../../constants/common'
import { EVENT_NAME_CLICK } from '../../constants/event'
import { toCSSPixel } from '../../utils/styles'
import { isThemeDark } from '../../utils/themeMode'
import { getModalContentFullScreenHeight } from '../Modal'

import {
  WYSIWYG_EDITOR_CONTENT_STYLE_DARK,
  WYSIWYG_EDITOR_CONTENT_STYLE_LIGHT,
  WYSIWYG_EDITOR_DEFAULT_CARET_SCROLL_MARGIN_BOTTOM,
  WYSIWYG_EDITOR_DEFAULT_MAX_HEIGHT,
  WYSIWYG_EDITOR_DEFAULT_MIN_HEIGHT,
  WYSIWYG_EDITOR_EVENT_RESIZE_CONTENT,
  WYSIWYG_EDITOR_OVERFLOW_HIDDEN,
  WYSIWYG_EDITOR_SCROLLABLE_OVERFLOW_REGEXP,
  WYSIWYG_EDITOR_SKIN_DARK,
  WYSIWYG_EDITOR_SKIN_LIGHT,
} from './WysiwygEditor.constants'

// Dark sets only color-scheme: Chrome copies authored colors into merged blocks
function getWysiwygEditorThemeContentStyle(theme) {
  return isThemeDark(theme)
    ? WYSIWYG_EDITOR_CONTENT_STYLE_DARK
    : WYSIWYG_EDITOR_CONTENT_STYLE_LIGHT
}

function getElementAncestors(element) {
  const parent = element.parentElement

  return parent ? [parent, ...getElementAncestors(parent)] : EMPTY_LIST
}

function isScrollableElement(element) {
  return (
    WYSIWYG_EDITOR_SCROLLABLE_OVERFLOW_REGEXP.test(
      getComputedStyle(element).overflowY
    ) && element.scrollHeight > element.clientHeight
  )
}

// the iframe document scrolls once `autoresize` hits `max_height`
function getEditorContentScrollingElement(editor) {
  const { scrollingElement } = editor.getDoc()

  return editor.getBody().style.overflowY !== WYSIWYG_EDITOR_OVERFLOW_HIDDEN &&
    scrollingElement.scrollHeight > scrollingElement.clientHeight
    ? [scrollingElement]
    : EMPTY_LIST
}

function getEditorScrollParents(editor) {
  return [
    ...getEditorContentScrollingElement(editor),
    ...getElementAncestors(editor.getContainer()).filter(isScrollableElement),
  ]
}

function getEditorScrollParentRect(editor, scrollParent) {
  return (
    scrollParent === editor.getDoc().scrollingElement
      ? editor.iframeElement
      : scrollParent
  ).getBoundingClientRect()
}

function getEditorViewportRect(editor, rect) {
  const iframeRect = editor.iframeElement.getBoundingClientRect()

  return {
    bottom: iframeRect.top + rect.bottom,
    left: iframeRect.left + rect.left,
    right: iframeRect.left + rect.right,
    top: iframeRect.top + rect.top,
  }
}

function isRectOverlapping(firstRect, secondRect) {
  return (
    firstRect.top < secondRect.bottom &&
    firstRect.bottom > secondRect.top &&
    firstRect.left < secondRect.right &&
    firstRect.right > secondRect.left
  )
}

function getRectOverflowDelta({ marginBottom, parentRect, rect }) {
  const overflowBottom = rect.bottom - (parentRect.bottom - marginBottom)
  const overflowTop = rect.top - parentRect.top

  return overflowBottom > 0 ? overflowBottom : Math.min(overflowTop, 0)
}

function scrollElementBy(element, delta) {
  const prevScrollTop = element.scrollTop

  element.scrollTop += delta

  return element.scrollTop - prevScrollTop
}

function scrollEditorParentsBy(editor, delta) {
  getEditorScrollParents(editor).reduce(
    (restDelta, scrollParent) =>
      restDelta - scrollElementBy(scrollParent, restDelta),
    delta
  )
}

function scrollEditorRectIntoView({ editor, marginBottom, rect }) {
  getEditorScrollParents(editor).reduce((restRect, scrollParent) => {
    const appliedDelta = scrollElementBy(
      scrollParent,
      getRectOverflowDelta({
        marginBottom,
        parentRect: getEditorScrollParentRect(editor, scrollParent),
        rect: restRect,
      })
    )

    return {
      bottom: restRect.bottom - appliedDelta,
      top: restRect.top - appliedDelta,
    }
  }, rect)
}

export function addWysiwygEditorBorderValidation(nodes, error) {
  if (error) {
    nodes[0].style.border = '1px solid var(--color-error)'
  } else {
    nodes[0].style.border = EMPTY_STRING
  }
}

export function simulateWysiwygEditorClickEvent() {
  const simulate = new MouseEvent(EVENT_NAME_CLICK, {
    bubbles: true,
    cancelable: true,
    clientX: 100,
    view: window,
  })

  document.body.dispatchEvent(simulate)
}

export function getWysiwygEditorToolbar({
  enableInlineImageAttach = false,
  enableTables = false,
}) {
  const toolbarGroups = [
    ['fontselect', 'fontsizeselect'],
    ['bold', 'italic', 'underline', 'forecolor'],
    ['alignroup'],
    ['numlist', 'bullist'],
    ['link'],
    ['outdent', 'indent'],
    ['blockquote', 'removeformat'],
  ]

  if (enableInlineImageAttach) {
    toolbarGroups.push(['customImageUploading'])
  }

  if (enableTables) {
    toolbarGroups.push(['table'])
  }

  return toolbarGroups.map(group => group.join(' ')).join(' | ')
}

// 0 outside a modal box, where the content has no fixed height
export function getWysiwygEditorFullScreenHeight({ content, editor, wrapper }) {
  if (!getModalContentFullScreenHeight(content)) {
    return 0
  }

  const wrapperHeight = wrapper.getBoundingClientRect().height
  const editorHeight = editor.container.getBoundingClientRect().height

  return Math.floor(content.clientHeight - (wrapperHeight - editorHeight))
}

export function getWysiwygEditorHeightLimits(editor) {
  return {
    maxHeight: editor.getParam(
      'max_height',
      WYSIWYG_EDITOR_DEFAULT_MAX_HEIGHT,
      'number'
    ),
    minHeight: editor.getParam(
      'min_height',
      WYSIWYG_EDITOR_DEFAULT_MIN_HEIGHT,
      'number'
    ),
  }
}

// `autoresize` reads the limits from `editor.settings` on every resize
export function setWysiwygEditorHeightLimits(editor, { maxHeight, minHeight }) {
  editor.settings.min_height = minHeight
  editor.settings.max_height = maxHeight
  editor.container.style.minHeight = toCSSPixel(minHeight)

  editor.fire(WYSIWYG_EDITOR_EVENT_RESIZE_CONTENT)
}

export function getWysiwygEditorContentStyle({
  blocked,
  contentStyles,
  defaultFont,
  theme,
}) {
  const { bodyStyles = EMPTY_STRING, commonStyles = EMPTY_STRING } =
    contentStyles

  return `body {
        font-family: ${defaultFont.name}, ${defaultFont.family};
        font-size: ${defaultFont.size}px;
        ${bodyStyles} 
      }
      ${getWysiwygEditorThemeContentStyle(theme)}
      p {
        line-height: 16px;
        margin: 0;
      }

      p img {
        max-width: 100%;
      }
      .mce-content-readonly {
        ${!blocked ? 'opacity: 0.5;' : EMPTY_STRING}
        cursor: not-allowed; 
      }
      ${commonStyles}
      `
}

export function getWysiwygEditorTableConfig({ enableTables = false }) {
  return enableTables
    ? {
        contextmenu: 'table',
        table_toolbar:
          'tableprops tabledelete | ' +
          'tableinsertrowbefore tableinsertrowafter tabledeleterow | ' +
          'tableinsertcolbefore tableinsertcolafter tabledeletecol | ' +
          'tablemergecells tablesplitcells',
      }
    : {
        contextmenu: false,
      }
}

export function getWysiwygEditorLinkConfig({ keepLinkUrls = false }) {
  return keepLinkUrls
    ? {
        convert_urls: false,
        link_default_protocol: 'https',
      }
    : {
        relative_urls: false,
        remove_script_host: false,
      }
}

export function getWysiwygEditorCaretRect(editor) {
  const [rangeRect] = editor.selection.getRng().getClientRects()

  return getEditorViewportRect(
    editor,
    rangeRect || editor.selection.getNode().getBoundingClientRect()
  )
}

export function isWysiwygEditorCaretOverElement(editor, element) {
  return isRectOverlapping(
    getWysiwygEditorCaretRect(editor),
    element.getBoundingClientRect()
  )
}

export function scrollWysiwygEditorCaretIntoView({
  editor,
  marginBottom = WYSIWYG_EDITOR_DEFAULT_CARET_SCROLL_MARGIN_BOTTOM,
}) {
  scrollEditorRectIntoView({
    editor,
    marginBottom,
    rect: getWysiwygEditorCaretRect(editor),
  })
}

export function scrollWysiwygEditorElementIntoView({
  editor,
  element,
  marginBottom = WYSIWYG_EDITOR_DEFAULT_CARET_SCROLL_MARGIN_BOTTOM,
}) {
  scrollEditorRectIntoView({
    editor,
    marginBottom,
    rect: getEditorViewportRect(editor, element.getBoundingClientRect()),
  })
}

export function restoreWysiwygEditorCaretViewportTop({ editor, top }) {
  scrollEditorParentsBy(editor, getWysiwygEditorCaretRect(editor).top - top)
}

export function getWysiwygEditorSkin(theme) {
  return isThemeDark(theme)
    ? WYSIWYG_EDITOR_SKIN_DARK
    : WYSIWYG_EDITOR_SKIN_LIGHT
}
